/**
 * User Store - Zustand store for current user state
 * Manages user profile data fetched from the API
 */

import api from "@lib/api";
import { API_ENDPOINTS } from "@lib/config/constants";
import { STORAGE_KEYS } from "@lib/config/storageKeys";
import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * Avatar document type - used in UserProfile
 */
export interface UserAvatar {
  readonly id: number;
  readonly storagePath?: string;
}

/**
 * User profile type - extend based on API response shape
 */
export interface UserProfile {
  readonly id: number;
  readonly email?: string;
  readonly nom?: string;
  readonly prenom?: string;
  readonly matricule?: string;
  readonly personneId?: number;
  readonly personne?: {
    readonly id: number;
    readonly nom?: string;
    readonly prenom?: string;
    readonly utilisateur?: {
      readonly avatar?: UserAvatar;
    };
  };
  readonly avatar?: UserAvatar;
  readonly roles?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface UserState {
  user: UserProfile | null;
}

export interface UserActions {
  setUser: (user: UserProfile) => void;
  clearUser: () => void;
}

export type UserStore = UserState & UserActions;

export const useUserStore = create<UserStore>()(
  persist(
    (set) => ({
      user: null,

      setUser: (user) => set({ user }),

      clearUser: () => set({ user: null }),
    }),
    {
      name: STORAGE_KEYS.USER.STORAGE,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

// Re-export for backward compatibility
export default useUserStore;

/**
 * Query hook for fetching a user by ID
 */
export function useUserQuery({ userId, ...options }) {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.USERS.DETAIL(userId));
      return response.data;
    },
    enabled: !!userId,
    ...options,
  });
}
